import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCohortPlaygroundProjects,
  type CohortPlaygroundProjectSummary,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Teacher read-only list of playground projects owned by a cohort's active students. */
export default function CohortPlaygroundListPage() {
  const { cohortId } = useParams();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [projects, setProjects] = useState<CohortPlaygroundProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!bearer || !cohortId) return;
    setError(null);
    setLoading(true);
    try {
      const data = await getCohortPlaygroundProjects(bearer, cohortId);
      setProjects(data.projects);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load student playground projects");
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <main className="page page-align-start">
      <div className="card card-wide">
        <header className="row-header">
          <div>
            <p className="eyebrow">Cohort · Playground</p>
            <h1>Student playgrounds</h1>
            <p className="lede">Read-only view of playgrounds created by students enrolled in this cohort.</p>
          </div>
          <div className="actions inline">
            <Link to={`/teacher/cohorts/${cohortId ?? ""}`} className="btn secondary">
              ← Cohort
            </Link>
          </div>
        </header>

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <p className="muted">Loading playgrounds…</p>
        ) : projects.length === 0 ? (
          <p className="muted">No playgrounds from this cohort's students yet.</p>
        ) : (
          <table className="teacher-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Playground</th>
                <th>Files</th>
                <th>Last updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id}>
                  <td>{p.ownerEmail}</td>
                  <td>
                    <strong>{p.title}</strong>
                  </td>
                  <td>{p.fileCount}</td>
                  <td>{formatTimestamp(p.updatedAt)}</td>
                  <td className="teacher-table-actions">
                    <Link
                      to={`/teacher/cohorts/${cohortId ?? ""}/playground/${p.id}`}
                      className="btn secondary tight"
                      style={{ textDecoration: "none" }}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
